'use client';
import { ScrollAreaScrollbar, ScrollAreaThumb, ScrollAreaViewport } from '@radix-ui/react-scroll-area';
import * as Select from '@radix-ui/react-select';
import { Dispatch, SetStateAction, useState } from 'react';

import { useTranslation } from '@lichtblick/i18next';
import { KeyboardArrowDown, KeyboardArrowUp } from '@lichtblick/icons';
import { Colors, TextVariants } from '@lichtblick/theme';
import { Option } from '@lichtblick/types';

import { RadioItem, ScrollAreaRoot, SelectContent, SelectItem, SelectTrigger } from './Dropdown.styles';

import { Text } from '../Text';

type DropdownProps = {
  defaultValue?: string;
  isBorderless?: boolean;
  isNullable?: boolean;
  isSmall?: boolean;
  options: Option[] | undefined;
  placeholder?: string;
  selectedOption: string | number | undefined;
  setSelectedOption: Dispatch<SetStateAction<Option | undefined>>;
};

export const Dropdown = ({
  defaultValue,
  isBorderless = false,
  isNullable,
  isSmall,
  options,
  placeholder = 'Select',
  selectedOption,
  setSelectedOption,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const handleValueChange = (value: string) => {
    if (value === 'empty') {
      setSelectedOption(undefined);
    } else {
      setSelectedOption(options?.find((option) => option.value?.toString() === value));
    }
  };

  return (
    <Select.Root
      defaultValue={defaultValue}
      onOpenChange={setIsOpen}
      onValueChange={handleValueChange}
      value={selectedOption?.toString() ?? 'empty'}
    >
      <SelectTrigger $isBorderless={isBorderless} $isSmall={!!isSmall}>
        <Select.Value placeholder={placeholder} />
        <Select.Icon asChild>{isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</Select.Icon>
      </SelectTrigger>
      <Select.Portal>
        <SelectContent position="popper">
          <ScrollAreaRoot $isActive={!!(options?.length && options.length > 8)} type="auto">
            <Select.Viewport asChild>
              <ScrollAreaViewport style={{ height: '20rem', overflowX: 'auto', overflowY: 'auto' }}>
                {isNullable && (
                  <SelectItem key="empty" value="empty">
                    <Select.ItemText>
                      <Text $variant={TextVariants.textfield.m}>{t('common:all')}</Text>
                    </Select.ItemText>
                  </SelectItem>
                )}
                {isBorderless
                  ? options?.map((option) => (
                      <SelectItem key={option.value} value={option.value?.toString() ?? 'undefined'}>
                        <RadioItem $isSelected={selectedOption?.toString() === option.value?.toString()} />
                        <Select.ItemText>
                          <Text $variant={TextVariants.textfield.m}>{option.label}</Text>
                        </Select.ItemText>
                      </SelectItem>
                    ))
                  : options?.map((option) => (
                      <SelectItem key={option.value} value={option.value?.toString() ?? 'undefined'}>
                        {option.icon}
                        <Select.ItemText>
                          <Text $variant={TextVariants.textfield.m}>{option.label}</Text>
                        </Select.ItemText>
                      </SelectItem>
                    ))}
              </ScrollAreaViewport>
            </Select.Viewport>
            <ScrollAreaScrollbar
              orientation="vertical"
              style={{
                display: 'flex',
                userSelect: 'none',
                touchAction: 'none',
                padding: '2px',
              }}
            >
              <ScrollAreaThumb
                style={{
                  width: '4px',
                  borderRadius: '4px',
                  flex: 1,
                  background: Colors.blue[20],
                  position: 'relative',
                }}
              />
            </ScrollAreaScrollbar>
          </ScrollAreaRoot>
        </SelectContent>
      </Select.Portal>
    </Select.Root>
  );
};
